import React, { useEffect, useRef, useState } from 'react';
import { Breadcrumb, Col } from 'react-bootstrap';
import Row from 'react-bootstrap/esm/Row';
import CompletedQ from './CompletedQ';
import { Link, useLocation } from 'react-router-dom';
import userService from '../../app/services/user.service';
import Loader from '../../UI/loader/loader';
import SecBTN from '../../UI/SecBTN';
import Modals from "../../UI/Modals/Modals";
import './CompletedQues.css';
import './PrintView.css';   // reuse the same print styles

/* ── helpers (same as PrintView) ── */
const fixImgSrc = (html) => {
  if (!html) return '';
  return html
    .replace(/src="ckfinder\//gi, 'src="https://quizmaster.motion.ac.in/ckfinder/')
    .replace(/src="\/ckfinder\//gi, 'src="https://quizmaster.motion.ac.in/ckfinder/');
};

const splitOptions = (raw) => {
  if (!raw) return [];
  const parts = raw.includes('@@@') ? raw.split('@@@') : raw.split('@');
  return [...new Set(parts.filter(Boolean))];
};

const hasContent = (html) =>
  html &&
  html.trim() !== '' &&
  !html.includes('<body></body>') &&
  html.trim() !== '<p></p>';

/* ── single question row for print (same layout as PrintView) ── */
const PrintQuestionRow = ({ data, index, bilingual = false }) => {
  const engOptions = splitOptions(data.eng_option);
  const hindiOptions = splitOptions(data.h_option);
  const showHindi = bilingual && (hasContent(data.q_text_hindi) || hindiOptions.length > 0);

  return (
    <div className="pv-question-block">
      {/* META LINE 1 */}
      <div className="pv-meta-row1">
        <span>Q. No: {data.qno ?? index + 1}</span>
        <span>Qid: {data.qid}</span>
        {data.subject && <span>{data.subject}</span>}
        {data.qus_type && <span>{data.qus_type}</span>}
        {data.toughness && <span>{data.toughness}</span>}
      </div>
      {/* META LINE 2 */}
      {(data.topic_name || data.sub_topic_name) && (
        <div className="pv-meta-row2">
          {data.topic_name && <span>{data.topic_name}</span>}
          {data.sub_topic_name && <span>{data.sub_topic_name}</span>}
        </div>
      )}

      {/* QUESTION BODY */}
      <div className={`pv-body ${showHindi ? 'pv-two-col' : ''}`}>
        <div className="pv-col">
          <div className="pv-qtext" dangerouslySetInnerHTML={{ __html: fixImgSrc(data.q_text) }} />
          {engOptions.length > 0 && (
            <ol className="pv-options">
              {engOptions.map((opt, i) => (
                <li key={i}><div dangerouslySetInnerHTML={{ __html: fixImgSrc(opt) }} /></li>
              ))}
            </ol>
          )}
        </div>
        {showHindi && (
          <div className="pv-col pv-col-hindi">
            <div className="pv-qtext" dangerouslySetInnerHTML={{ __html: fixImgSrc(data.q_text_hindi) }} />
            {hindiOptions.length > 0 && (
              <ol className="pv-options">
                {hindiOptions.map((opt, i) => (
                  <li key={i}><div dangerouslySetInnerHTML={{ __html: fixImgSrc(opt) }} /></li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>

      {/* ANSWER BAR */}
      <div className="pv-answer-bar">
        Answer: <span>{data.answer}</span>
      </div>

      {/* SOLUTION */}
      {(hasContent(data.sol_text) || (bilingual && hasContent(data.sol_text_hindi))) && (
        <div className={`pv-solution ${bilingual ? 'pv-two-col' : ''}`}>
          <div className="pv-col">
            {hasContent(data.sol_text)
              ? <div dangerouslySetInnerHTML={{ __html: fixImgSrc(data.sol_text) }} />
              : <span className="pv-na">English Solution Not Available</span>}
          </div>
          {bilingual && <div className="pv-col pv-col-hindi">
            {hasContent(data.sol_text_hindi)
              ? <div dangerouslySetInnerHTML={{ __html: fixImgSrc(data.sol_text_hindi) }} />
              : <span className="pv-na">Hindi Solution Not Available</span>}
          </div>}
        </div>
      )}

      <hr className="pv-separator" />
    </div>
  );
};

/* ── main component ── */
const CompletedQues = () => {
  const { state } = useLocation();
  const { uid, sid, pid } = state;
  const [qData, setQData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPopUp, setShowPopUp] = useState(false);
  const [showPopUp1, setShowPopUp1] = useState(false);
  const reportTemplateRef = useRef(null);

  const paperDetailHandler = () => setShowPopUp(true);
  const viewSyllabusHandler = () => setShowPopUp1(true);
  const hideHandler = () => {
    setShowPopUp(false);
    setShowPopUp1(false);
  };

  useEffect(() => {
    setLoading(true);
    userService.getViewPaper({ uid, sid, pid })
      .then(data => { setQData(data.data); })
      .then(() => { setLoading(false); })
      .catch((error) => { setLoading(false); console.log("error", error); });
  }, []);

  const questions = qData?.paper_matrix ? Object.values(qData.paper_matrix) : [];
  const info = qData?.paper_info || {};

  return (
    <div>
      {loading && <Loader />}

      {/* ===== SCREEN-ONLY UI ===== */}
      <div className="no-print">
        {/* Paper Details Modal */}
        <Modals show={showPopUp} onHide={hideHandler} size="lg">
          <h4 className="mb-4">Paper Details</h4>
          {qData?.paper_info && (
            <Row className="mt-3">
              <Col md={6} sm={12}><b>Paper Code:</b> {qData.paper_info.paper_id}</Col>
              <Col md={6} sm={12}><b>Paper Name:</b> {qData.paper_info.paper_name}</Col>
              <Col md={6} sm={12}><b>Batch:</b> {qData.paper_info.batch_name || 'NA'}</Col>
              <Col md={6} sm={12}><b>Target:</b> {qData.paper_info.target || 'NA'}</Col>
              <Col md={6} sm={12}><b>Session:</b> {qData.paper_info.Session || 'NA'}</Col>
              <Col md={6} sm={12}><b>Center:</b> {qData.paper_info.centers || 'NA'}</Col>
              <Col md={6} sm={12}><b>Class:</b> {qData.paper_info.classes || 'NA'}</Col>
              <Col md={6} sm={12}><b>Paper Requested By:</b> {qData.paper_info.creator || 'NA'}</Col>
              <Col md={6} sm={12}>
                <b>Paper Requested On:</b>{' '}
                {qData.paper_info.created_on ? new Date(qData.paper_info.created_on).toLocaleString() : 'NA'}
              </Col>
              <Col md={6} sm={12}><b>Format:</b> {qData.paper_info.paper_format || 'NA'}</Col>
              <Col md={6} sm={12}><b>Exam On:</b> {qData.paper_info.to_be_held_on || 'NA'}</Col>
              <Col md={6} sm={12}><b>Last Date of Submission:</b> {qData.paper_info.to_be_completed_by || 'NA'}</Col>
              <Col md={6} sm={12}><b>Phase:</b> {qData.paper_info.phase || 'NA'}</Col>
              <Col md={12} sm={12}>
                <div className="d-flex">
                  <b>Questions Assigned:</b>
                  <div>
                    {qData.paper_matrix && Object.values(qData.paper_matrix)
                      .sort((a, b) => a.sec_id - b.sec_id)
                      .map((data, idx) => (
                        <div className="d-flex ps-1" key={data.sec_id || idx}>
                          <b>{data.section_name ? `${data.section_name} : ` : ''}</b>
                          <span className="ps-1">{data.finalQues || ''}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Modals>

        {/* Syllabus Modal */}
        <Modals show={showPopUp1} onHide={hideHandler} size="lg">
          <h4 className="mb-4">Syllabus</h4>
          <p><span className="text-muted">Topic : </span>{qData?.paper_info?.topic_names}</p>
          <p><span className="text-muted">Remarks : </span>{qData?.paper_info?.topic_remarks}</p>
        </Modals>

        {/* Header bar */}
        <div className="section completed-ques">
          <div className="d-flex justify-content-between align-items-center gap-1">
            <div>
              <h4>Questions</h4>
              <Breadcrumb>
                <Breadcrumb.Item><Link to="/createPaper">Create Paper</Link></Breadcrumb.Item>
                <Breadcrumb.Item active>Completed</Breadcrumb.Item>
                <Breadcrumb.Item active>Questions</Breadcrumb.Item>
              </Breadcrumb>
            </div>
            <div>
              <SecBTN className="sec-btn" onClick={paperDetailHandler}>Paper Details</SecBTN>
              <SecBTN className="sec-btn" onClick={viewSyllabusHandler}>View Syllabus</SecBTN>
              <a
                href={`/createPaper/printView?uid=${uid}&sid=${sid}&pid=${pid}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <SecBTN className="sec-btn1">Print</SecBTN>
              </a>
            </div>
          </div>

          {/* Screen question list */}
          <div ref={reportTemplateRef}>
            <Row className="mx-0">
              {qData?.paper_matrix && Object.values(qData.paper_matrix).map((data, i) => (
                <CompletedQ
                  key={data.qid || i}
                  qid={data?.qid}
                  qNo={data?.qno}
                  topic={data?.topic_name}
                  subTopic={data?.sub_topic_name}
                  toughness={data?.toughness}
                  question={data?.q_text}
                  hindiQuestion={data?.q_text_hindi}
                  engOption={data?.eng_option}
                  hindiOption={data?.h_option}
                  correctAns={data?.answer}
                  answer={data?.sol_text}
                  hindiAnswer={data?.sol_text_hindi}
                />
              ))}
            </Row>
          </div>

          {Number(qData?.paper_info?.is_final) !== 1 && (
            <div className="d-flex justify-content-center m-3">
              <Link
                to="/createPaper/paperMatrix"
                state={{ uid, sid, pid }}
              >
                <SecBTN className="sec-btn1 justify-content-center">
                  Edit Matrix
                </SecBTN>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* ===== PRINT-ONLY LAYOUT (same design as PrintView / legacy PHP) ===== */}
      <div className="print-only pv-root printable">
        {/* Paper info panel */}
        <div className="pv-panel">
          <div className="pv-panel-header">
            <span>{info.paper_name || 'Paper Details'}</span>
          </div>
          <div className="pv-panel-body">
            {info.batch_name && <div><b>Batch:</b> {info.batch_name}</div>}
            {info.Session && <div><b>Session:</b> {info.Session}</div>}
            {info.centers && <div><b>Center:</b> {info.centers}</div>}
            {info.classes && <div><b>Class:</b> {info.classes}</div>}
            {info.target && <div><b>Target:</b> {info.target}</div>}
            {info.phase && <div><b>Phase:</b> {info.phase}</div>}
            {info.creator && <div><b>Paper Request by:</b> {info.creator}</div>}
            {info.to_be_held_on && <div><b>Exam On:</b> {info.to_be_held_on}</div>}
            {info.to_be_completed_by && <div><b>Last Date of Submission:</b> {info.to_be_completed_by}</div>}
            {info.paper_format && <div><b>Format:</b> {info.paper_format}</div>}
            {info.topic_names && <div><b>Syllabus:</b> {info.topic_names}</div>}
            {info.topic_remarks && <div><b>Remarks:</b> {info.topic_remarks}</div>}
          </div>
        </div>

        {/* Questions panel */}
        <div className="pv-panel">
          <div className="pv-panel-header">
            <span>Questions</span>
          </div>
          <div className="pv-panel-body pv-questions">
            {questions.map((data, i) => (
              <PrintQuestionRow key={data.qid || i} data={data} index={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletedQues;
